import React from 'react';
import {
  Box, Flex, Text, Tooltip, useColorModeValue,
} from '@chakra-ui/react';
import { useOnlineUsers } from '../hooks/usePresence';
import { useAppearance } from '../theme/AppearanceProvider';
import { userColor, userInitials } from '../theme/userColor';

// item 318/320 — the "who's online" cluster in the KPI strip. Restyled from a row of loud
// solid-color blobs into a quiet overlapping avatar stack: photos render full-bleed; the
// initials fallback is a soft translucent chip that carries the user's identity color only
// as a ring + text tint, so it sits calmly on ANY tenant brand chrome instead of fighting
// it. Skin-aware: flat paper chips on Accountant.
const CHIP = 26; // px

const OnlineNow: React.FC = () => {
  const { data } = useOnlineUsers();
  const { aurora } = useAppearance();
  // the overlap ring matches the strip surface so chips read as a single cluster
  const ringColor = useColorModeValue('rgba(255,255,255,0.85)', 'rgba(20,20,28,0.85)');
  const fallbackBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const users = data ?? [];
  if (users.length === 0) return null;
  const shown = users.slice(0, 6);
  return (
    <Flex align="center" aria-label="online teammates">
      {shown.map((u, i) => {
        const color = userColor(u.id);
        const fallbackChipBg = aurora ? fallbackBg : 'ledgerSurface';
        return (
          <Tooltip key={u.id} label={`${u.name} · online`} hasArrow>
            {/* span wrapper so the Tooltip has a DOM ref target */}
            <Box as="span" ml={i === 0 ? 0 : '-7px'} zIndex={shown.length - i} position="relative">
              <Flex
                w={`${CHIP}px`}
                h={`${CHIP}px`}
                align="center"
                justify="center"
                borderRadius="full"
                fontSize="0.6rem"
                fontWeight="bold"
                overflow="hidden"
                bg={u.photoUrl ? undefined : fallbackChipBg}
                color={aurora ? color.bg : 'ledgerInk'}
                backgroundImage={u.photoUrl ? `url(${u.photoUrl})` : undefined}
                backgroundSize="cover"
                backgroundPosition="center"
                backdropFilter={aurora && !u.photoUrl ? 'var(--bx-glass-chip)' : undefined}
                boxShadow={aurora
                  ? `0 0 0 2px ${ringColor}, 0 0 0 3px ${color.bg}55`
                  : `0 0 0 2px ${ringColor}`}
                border={aurora ? undefined : '1px solid'}
                borderColor={aurora ? undefined : 'ledgerStroke'}
              >
                {!u.photoUrl && userInitials(u.name)}
              </Flex>
              {/* online pulse dot on the front chip only — one quiet signal, not six */}
              {i === 0 && (
                <Box
                  position="absolute"
                  bottom="-1px"
                  right="-1px"
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg="green.400"
                  boxShadow={`0 0 0 2px ${ringColor}`}
                />
              )}
            </Box>
          </Tooltip>
        );
      })}
      {users.length > 6 && (
        <Text fontSize="xs" opacity={0.7} ml={2}>{`+${users.length - 6}`}</Text>
      )}
    </Flex>
  );
};

export default OnlineNow;
